import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  animationFrameScheduler,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  throttleTime
} from 'rxjs';

import { ResizeService, WindowRefService } from '@hdms/shared/util-core';

import { FloatingElement } from './floating.types';

export interface FloatingStackingServiceElement extends FloatingElement {
  calculatedOffset?: number;
  updated?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FloatingStackingService {
  floatingElements$ = new BehaviorSubject<FloatingStackingServiceElement[]>([]);
  floatingElementsWithOffset$: Observable<FloatingStackingServiceElement[]>;
  resize$: Observable<void | Window>;
  private scrollContainer: Window | HTMLElement = this.getDefaultScrollContainer();

  constructor(private readonly resizeService: ResizeService, private readonly winRef: WindowRefService) {
    this.resize$ = this.resizeService.onResize$.pipe(throttleTime(0, animationFrameScheduler));
    const isNotEmpty = a => a && a.length > 0;

    /* Recalculate offsets when either window is resized or the elements changed */
    this.floatingElementsWithOffset$ = combineLatest([this.floatingElements$, this.resize$.pipe(startWith(''))]).pipe(
      filter(([stickyElements]) => isNotEmpty(stickyElements)),
      map(([stickyElementsList]) => this.calculatePositions(stickyElementsList))
    );
  }

  static compareStacking(se1: FloatingStackingServiceElement, se2: FloatingStackingServiceElement): number {
    if (se1.isSmartbar === se2.isSmartbar && se1.isToastMessage === se2.isToastMessage) {
      return FloatingStackingService.sortStandardElements(se1, se2);
    } else {
      if (se1.isSmartbar || se2.isSmartbar) {
        return se1.isSmartbar ? -1 : 1;
      }
      if (se1.isToastMessage || se2.isToastMessage) {
        return se1.isToastMessage ? 1 : -1;
      }
      return FloatingStackingService.sortStandardElements(se1, se2);
    }
  }

  static sortStandardElements(se1: FloatingStackingServiceElement, se2: FloatingStackingServiceElement): number {
    if (se1.stacking === se2.stacking) {
      return se2.updated - se1.updated;
    } else {
      return se1.stacking ? 1 : -1;
    }
  }

  private calculatePositions(stickyElementList: FloatingStackingServiceElement[]) {
    /* Initialized calculatedPositions. They start with 0. Height and offsets of elements that stack are added on top of them */
    let calculatedPosition = 0;

    /* Calculate offsets for each element based on priority (already sorted by priority) */
    return stickyElementList.map(stickyElement => {
      const elementPos = calculatedPosition + stickyElement.offset;
      if (stickyElement.stacking || stickyElement.isSmartbar) {
        calculatedPosition = elementPos + stickyElement.nativeElement.offsetHeight;
      }
      return {
        ...stickyElement,
        calculatedOffset: elementPos
      };
    });
  }

  /*
   * Returns an observable that emits whenever the calculated offset of the passed element changed.
   * Only returns a value for registered elements so make sure to register the corresponding element
   * when you want to track offset.
   */
  getCalculatedOffset(nativeElement): Observable<number> {
    return this.floatingElementsWithOffset$.pipe(
      map(
        (stickyElements: FloatingStackingServiceElement[]) =>
          stickyElements.filter(se => se.nativeElement.isEqualNode(nativeElement))[0]
      ),
      filter(el => !!el),
      map(data => data.calculatedOffset),
      distinctUntilChanged()
    );
  }

  /*
   * Update a single value of any already registered element. E.g. if it is stacking or it's offsets.
   */
  updateValue(nativeElement: HTMLElement, prop: string, value: any) {
    const currentElements = this.floatingElements$.getValue();
    const elem = currentElements.find(se => se.nativeElement.isEqualNode(nativeElement));
    if (elem) {
      elem[prop] = value;
      elem.updated = new Date().getTime();
      this.floatingElements$.next(this.sortByStacking(currentElements));
    }
  }

  /*
   * Register a new element as sticky element. This element will be tracked by the service from here on. Make sure
   * to deregister it again when element is destroyed.
   */
  registerElement(stickyElement: FloatingElement): void {
    this.addElement(stickyElement as FloatingStackingServiceElement);
  }

  /* Stops tracking of the defined element */
  deregisterElement(element: HTMLElement) {
    this.removeElement(element);
  }

  private addElement(stickyElement: FloatingStackingServiceElement) {
    this.floatingElements$.next(
      this.sortByStacking([{ ...stickyElement, updated: new Date().getTime() }, ...this.floatingElements$.getValue()])
    );
  }

  private removeElement(element: HTMLElement) {
    this.floatingElements$.next(this.floatingElements$.getValue().filter(e => !e.nativeElement.isEqualNode(element)));
  }
  /*
   * Sorts an array of sticky elements by the following priority:
   * 1. Is smartbar
   * 2. Is stacking (Non stacking elements come first)
   * 3. The last time its values have been updated
   * 4. Is toastMessage
   */
  private sortByStacking(stickyElements: FloatingStackingServiceElement[]) {
    return stickyElements.sort(FloatingStackingService.compareStacking);
  }

  /*
   * Registeres a new scroll container for this service. One service can only have one scroll container.
   * Registering is done to tell the floating elements which scroll container they should track the scroll event for.
   */
  setScrollContainer(el: HTMLElement) {
    if (this.scrollContainer instanceof HTMLElement) {
      console.warn('Warning: This instance of FloatingStackingService already has an assigned scroll container.');
    }
    this.scrollContainer = el;
  }

  removeScrollContainer(el: HTMLElement) {
    if (this.scrollContainer instanceof HTMLElement && el.isEqualNode(this.scrollContainer)) {
      this.scrollContainer = this.getDefaultScrollContainer();
    }
  }

  getScrollcontainer() {
    return this.scrollContainer;
  }

  private getDefaultScrollContainer(): Window {
    return this.winRef.nativeWindow;
  }
}
